'use client';

import { Table } from '@tanstack/react-table';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import type { UseDataTableExportReturn } from './use-data-table-export';
import { getTableHeader } from './utils';

export interface UseDataTableExportClientOptions<TData = any> {
  /** 表格实例 */
  table: Table<TData>;
  /** 工作表名称（默认：Sheet1） */
  sheetName?: string;
  /**
   * 数据转换函数（在导出前应用）
   * 用于将原始数据转换为展示格式（如码值转中文、日期格式化等）
   */
  transformRow?: (row: TData) => Record<string, any>;
}

/**
 * 客户端表格导出 Hook
 *
 * 使用 ExcelJS 在浏览器中生成 Excel 文件，适用于小到中等数据量（<10000行）
 *
 * @example
 * ```typescript
 * const exporter = useDataTableExportClient({
 *   table,
 * });
 *
 * <Exporter {...exporter} filename="充电枪列表" />
 * ```
 */
export function useDataTableExportClient<TData = any>(
  options: UseDataTableExportClientOptions<TData>
): UseDataTableExportReturn {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const { table, sheetName = 'Sheet1', transformRow } = options;
  const totalRows = table.getRowCount();

  const handleExport = useCallback(
    async (filename: string) => {
      try {
        setLoading(true);
        setError(null);
        setProgress(0);

        // 获取表头配置（导出时过滤隐藏列）
        const header = getTableHeader(table);

        // 获取当前过滤和排序后的数据行，并应用转换函数（如果提供）
        const rows = table
          .getSortedRowModel()
          .rows.map((row) => (transformRow ? transformRow(row.original) : row.original));

        setProgress(30);

        // 提取数据列（最后一层的非占位符列）
        const lastLayer = header[header.length - 1];
        const dataColumns = lastLayer.filter((col) => !col.isPlaceholder && col.key);

        // 将数据转换为 Excel 行格式
        const excelRows = rows.map((rowData: any) => {
          const excelRow: Record<string, any> = {};
          dataColumns.forEach((col) => {
            if (col.key) {
              excelRow[col.key] = rowData[col.key] ?? '';
            }
          });
          return excelRow;
        });

        setProgress(60);

        // 使用 ExcelJS 生成 Excel 文件
        const workbook = await import('exceljs').then((m) => m.default);
        const wb = new workbook.Workbook();
        const worksheet = wb.addWorksheet(sheetName);

        // 设置列
        worksheet.columns = dataColumns.map((col) => ({
          key: col.key!,
          header: col.title,
          width: col.width || 30,
        }));

        // 添加数据行
        worksheet.addRows(excelRows);

        setProgress(80);

        // 生成 Excel 文件
        const buffer = await wb.xlsx.writeBuffer();

        setProgress(90);

        // 触发下载
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        setProgress(100);
      } catch (err) {
        setError(err instanceof Error ? err.message : '导出失败');
        toast.error(err instanceof Error ? err.message : '导出失败');
      } finally {
        setLoading(false);
      }
    },
    [table, sheetName, transformRow]
  );

  // 处理对话框状态变化
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !loading) {
        // 重置状态
        setProgress(0);
        setError(null);
      }
      setOpen(newOpen);
    },
    [loading]
  );

  return {
    open,
    onOpenChange: handleOpenChange,
    loading,
    progress,
    error,
    totalRows,
    handleExport,
  };
}
